import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { colors } from '../GlobalStyles';
import Logo from '../assets/logo.svg';

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${colors.primaryDark};
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }

  @media (max-width: 580px) {
    gap: 0.3rem;
  }
`;

const LogoImage = styled.img`
  height: 3rem;
  width: auto;

  @media (max-width: 768px) {
    height: 2.5rem;
  }

  @media (max-width: 580px) {
    height: 2rem;
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 580px) {
    /* Keep text visible on mobile but adjust styling for space */
    gap: 0;
  }
`;

const CompanyName = styled.h1`
  color: ${colors.accentOrange};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 580px) {
    font-size: 1.1rem;
  }
`;

const Tagline = styled.p`
  color: ${colors.white};
  font-size: 0.8rem;
  font-weight: 400;
  margin: 0;
  line-height: 1;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media (max-width: 580px) {
    font-size: 0.75rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 580px) {
    display: none; /* Will implement hamburger menu later */
  }
`;

const NavLink = styled.a`
  color: ${colors.white};
  font-weight: 400;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    color: ${colors.accentOrange};
  }
`;

const ContactButton = styled.button`
  background: transparent;
  color: ${colors.white};
  border: 2px solid ${colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${colors.white};
    color: ${colors.primaryDark};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;

const MobileMenuIcon = styled.div`
  display: none;
  flex-direction: column;
  cursor: pointer;

  @media (max-width: 580px) {
    display: flex;
  }
`;

const MenuBar = styled.div<{ isOpen?: boolean }>`
  width: 25px;
  height: 3px;
  background: ${colors.white};
  margin: 3px 0;
  transition: 0.3s;

  ${({ isOpen }) =>
    isOpen &&
    `
    &:nth-child(1) {
      transform: rotate(-45deg) translate(-5px, 6px);
    }
    &:nth-child(2) {
      opacity: 0;
    }
    &:nth-child(3) {
      transform: rotate(45deg) translate(-5px, -6px);
    }
  `}
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;

  @media (max-width: 580px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100%;
  background: ${colors.primaryDark};
  padding: 2rem;
  transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
  transition: transform 0.3s ease;
  z-index: 1001;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);

  @media (max-width: 580px) {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding-top: 5rem;
  }
`;

const MobileNavLink = styled.a`
  color: ${colors.white};
  font-weight: 400;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.accentOrange};
  }
`;

const MobileContactButton = styled.button`
  background: transparent;
  color: ${colors.white};
  border: 2px solid ${colors.white};
  padding: 1rem 1.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.white};
    color: ${colors.primaryDark};
  }
`;

export function Navigation() {
  const location = useLocation();
  const isHomepage = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false); // Close mobile menu when navigating

    if (!isHomepage) {
      // If not on homepage, navigate to homepage with hash
      window.location.href = `/#${sectionId}`;
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactClick = () => {
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
    window.open('https://www.linkedin.com/in/steve-widinghoff/', '_blank');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <NavContainer>
        <LogoLink to="/">
          <LogoSection>
            <LogoImage src={Logo} alt="Abrovinc Logo" />
            <LogoText>
              <CompanyName>Abrovinc</CompanyName>
              <Tagline>Smart solutions to tricky problems</Tagline>
            </LogoText>
          </LogoSection>
        </LogoLink>

        <NavLinks>
          <NavLink onClick={() => scrollToSection('about')}>What we do</NavLink>
          <NavLink onClick={() => scrollToSection('services')}>Our Services</NavLink>
          <NavLink onClick={() => scrollToSection('team')}>Meet our team</NavLink>
          <NavLink onClick={() => scrollToSection('contact-section')}>Contact us</NavLink>
          <ContactButton onClick={handleContactClick}>Connect on LinkedIn</ContactButton>
        </NavLinks>

        <MobileMenuIcon onClick={toggleMobileMenu}>
          <MenuBar isOpen={isMobileMenuOpen} />
          <MenuBar isOpen={isMobileMenuOpen} />
          <MenuBar isOpen={isMobileMenuOpen} />
        </MobileMenuIcon>
      </NavContainer>

      <MobileMenuOverlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileNavLink onClick={() => scrollToSection('about')}>What we do</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('services')}>Our Services</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('team')}>Meet our team</MobileNavLink>
        <MobileNavLink onClick={() => scrollToSection('contact-section')}>Contact us</MobileNavLink>
        <MobileContactButton onClick={handleContactClick}>Connect on LinkedIn</MobileContactButton>
      </MobileMenu>
    </>
  );
}
